'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Search, BookOpen, ArrowRight } from 'lucide-react'
import { getAllTerms, getAvailableLetters } from '@/lib/glossary/terms'
import { glossaryCategories } from '@/lib/glossary/config'

const allTerms = getAllTerms()
const availableLetters = getAvailableLetters()
const alphabet = ['#', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))]

export default function GlossaryPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null)

  const filteredTerms = useMemo(() => {
    let terms = allTerms

    if (searchQuery) {
      const lower = searchQuery.toLowerCase()
      terms = terms.filter(
        (term) =>
          term.title.toLowerCase().includes(lower) ||
          (term.abbreviation && term.abbreviation.toLowerCase().includes(lower)) ||
          term.definition.toLowerCase().includes(lower)
      )
    }

    if (selectedCategory) {
      terms = terms.filter((term) => term.category === selectedCategory)
    }

    if (selectedLetter) {
      terms = terms.filter((term) => {
        const firstChar = term.title.charAt(0).toUpperCase()
        if (selectedLetter === '#') return /[^A-Z]/.test(firstChar)
        return firstChar === selectedLetter
      })
    }

    return terms
  }, [searchQuery, selectedCategory, selectedLetter])

  const groupedTerms = useMemo(() => {
    const groups: Record<string, typeof filteredTerms> = {}
    filteredTerms.forEach((term) => {
      const firstChar = term.title.charAt(0).toUpperCase()
      const key = /[A-Z]/.test(firstChar) ? firstChar : '#'
      if (!groups[key]) groups[key] = []
      groups[key].push(term)
    })
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '#') return -1
      if (b === '#') return 1
      return a.localeCompare(b)
    })
  }, [filteredTerms])

  return (
    <div className="bg-gradient-to-b from-background to-muted/30 min-h-screen py-16 lg:py-24 dashboard-cards">
      <div className="container max-w-6xl mx-auto px-4">

        {/* Header */}
        <div className="text-center mb-12">
          <BookOpen className="h-16 w-16 text-primary mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">GovCon Glossary</h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            {allTerms.length} government contracting terms, acronyms, and definitions — from A to Z.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search terms, acronyms, or definitions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* A-Z Letter Navigation */}
        <div className="flex flex-wrap justify-center gap-1 mb-6">
          <button
            onClick={() => setSelectedLetter(null)}
            className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
              selectedLetter === null
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            All
          </button>
          {alphabet.map((letter) => {
            const hasTerms = availableLetters.includes(letter)
            return (
              <button
                key={letter}
                onClick={() => hasTerms ? setSelectedLetter(selectedLetter === letter ? null : letter) : undefined}
                disabled={!hasTerms}
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  selectedLetter === letter
                    ? 'bg-primary text-primary-foreground'
                    : hasTerms
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    : 'text-muted-foreground/30 cursor-not-allowed'
                }`}
              >
                {letter}
              </button>
            )
          })}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          <button onClick={() => setSelectedCategory(null)}>
            <Badge
              variant={selectedCategory === null ? 'default' : 'outline'}
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
            >
              All Categories
            </Badge>
          </button>
          {glossaryCategories.map((cat) => {
            const count = allTerms.filter((t) => t.category === cat.name).length
            if (count === 0) return null
            return (
              <button key={cat.slug} onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}>
                <Badge
                  variant={selectedCategory === cat.name ? 'default' : 'outline'}
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                >
                  {cat.name} ({count})
                </Badge>
              </button>
            )
          })}
        </div>

        {/* Results Count */}
        {(searchQuery || selectedCategory || selectedLetter) && (
          <p className="text-sm text-muted-foreground text-center mb-6">
            Showing {filteredTerms.length} of {allTerms.length} terms
          </p>
        )}

        {/* Grouped Terms */}
        {groupedTerms.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No terms found matching your criteria.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory(null); setSelectedLetter(null) }}
              className="mt-4 text-primary hover:underline text-sm"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {groupedTerms.map(([letter, terms]) => (
              <section key={letter} id={`letter-${letter}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-bold text-primary">{letter}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {terms.map((term) => (
                    <Link key={term.slug} href={`/glossary/${term.slug}`} className="block group">
                      <Card className="h-full bg-card/80 backdrop-blur-sm border rounded-lg shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-300">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base group-hover:text-primary transition-colors leading-tight">
                                {term.title}
                                {term.abbreviation && (
                                  <span className="text-muted-foreground font-normal"> ({term.abbreviation})</span>
                                )}
                              </CardTitle>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5 group-hover:translate-x-1 group-hover:text-primary transition-all duration-200" />
                          </div>
                          <Badge variant="secondary" className="w-fit text-xs mt-1">
                            {term.category}
                          </Badge>
                          <CardDescription className="text-sm leading-snug mt-2 line-clamp-2">
                            {term.definition}
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

      </div>
    </div>
  )
}
